import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { DocumentGroupFilter } from "@/components/documents/DocumentGroupFilter";
import { DocumentTypeFilter } from "@/components/documents/DocumentTypeFilter";
import { DocumentStatusFilter } from "@/components/documents/DocumentStatusFilter";
import { DocumentExport } from "@/components/documents/DocumentExport";
import { useAuth } from "@/contexts/AuthContext";

import BackofficeLayout from "@/components/layouts/BackofficeLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  MoreHorizontal,
  Plus,
  FileText,
  Loader2,
  FilePen,
  Trash,
  Copy,
  Eye,
  FileCheck,
  FileX,
  FileQuestion,
  Trash2,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Document, DocumentSearchParams } from "@/models/Document";
import { enhancedDocumentApi, documentGroupApi, documentApi, documentTypeApi } from "@/services/api";
import { useToast } from "@/hooks/use-toast";
import { PaginationControls } from "@/components/shared/PaginationControls";
import { DeleteDocumentDialog } from "@/components/documents/DeleteDocumentDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const Documents = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user, currentUser, userRoles, selectedRole, permissionsLoaded, loading, hasPermission, getCurrentRoleIds } = useAuth();
  
  const initialDocumentGroupIds = location.state?.document_group_ids || [];
  
  // Get API filters based on selected role - simplified to remove show_all/show_none
  const getApiFilters = () => {
    const filters: any = {
      search: "",
      document_group_ids: initialDocumentGroupIds,
      document_type_id: undefined,
      limit: 10,
      offset: 0,
    };

    // The backend will now handle role-based filtering automatically
    // No need to pass show_all, show_none, or management_company_id

    return filters;
  };

const [searchParams, setSearchParams] = useState<DocumentSearchParams>(getApiFilters());
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Update search params when selected role changes
  useEffect(() => {
    if (permissionsLoaded && selectedRole) {
      setSearchParams(prev => ({
        ...prev,
        ...getApiFilters()
      }));
    }
  }, [selectedRole, permissionsLoaded]);

  // State for duplicate dialog
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [duplicatingDocument, setDuplicatingDocument] = useState<Document | null>(null);
  const [newDocumentName, setNewDocumentName] = useState("");
  const [isDuplicating, setIsDuplicating] = useState(false);

  const { data: documentsResponse, isLoading, refetch } = useQuery({
    queryKey: ["documents", searchParams, selectedRole],
    queryFn: () => enhancedDocumentApi.list(searchParams),
    enabled: permissionsLoaded && !!selectedRole,
    meta: {
      onError: (error: Error) => {
        toast({
          title: "Error loading documents",
          description: error.message,
          variant: "destructive"
        });
      }
    }
  });

  const documents = documentsResponse?.data || [];
  const totalCount = documentsResponse?.meta?.total_count || 0;

  React.useEffect(() => {
    if (totalCount && searchParams.limit) {
      setTotalPages(Math.ceil(totalCount / searchParams.limit));
    }
  }, [totalCount, searchParams.limit]);

  const { data: documentGroups = [] } = useQuery({
    queryKey: ["document-groups"],
    queryFn: () => documentGroupApi.list(),
    enabled: permissionsLoaded,
    meta: {
      onError: (error: Error) => {
        console.error("Error loading document groups:", error);
      }
    }
  });

  const { data: documentTypes = [] } = useQuery({
    queryKey: ["document-types"],
    queryFn: () => documentTypeApi.list(),
    enabled: permissionsLoaded,
    meta: {
      onError: (error: Error) => {
        console.error("Error loading document types:", error);
      }
    }
  });

  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [deletingDocument, setDeletingDocument] = React.useState<Document | null>(null);
  const [isDeleting, setIsDeleting] = React.useState(false);

  // Check if user can create documents
  const canWriteDocument = () => {
    const currentRoleIds = getCurrentRoleIds();
    
    // Platform roles cannot create documents (only view them)
    if (selectedRole?.type === 'platform') {
      return false;
    }
    
    // Only management company and association roles can create documents
    return hasPermission(currentRoleIds, 'write', 'documents');
  };

  // The box echoes every keystroke instantly; only the QUERY is debounced —
  // binding the input straight to searchParams made typing lag and drop
  // letters (Mateo, 8-Sep), and firing a request per keystroke left the list
  // "loading" while the user was still typing (Mauro, 7-Sep).
  const [searchText, setSearchText] = useState<string>("");
  const searchTimer = useRef<number | null>(null);
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const searchTerm = e.target.value;
    setSearchText(searchTerm);
    if (searchTimer.current) window.clearTimeout(searchTimer.current);
    searchTimer.current = window.setTimeout(() => {
      setSearchParams((prev) => ({ ...prev, search: searchTerm, offset: 0 }));
      setCurrentPage(1);
    }, 400);
  };

  const handleGroupFilterChange = (groupIds: number[]) => {
    setSearchParams((prev) => ({ ...prev, document_group_ids: groupIds, offset: 0 }));
    setCurrentPage(1);
  };

  const handleTypeFilterChange = (typeId: number | undefined) => {
    setSearchParams((prev) => ({ ...prev, document_type_id: typeId, offset: 0 }));
    setCurrentPage(1);
  };

  const handleStatusFilterChange = (status: 'active' | 'expired' | 'missing' | undefined) => {
    setSearchParams((prev) => ({ ...prev, status: status, offset: 0 }));
    setCurrentPage(1);
  };

  const getDocumentGroupNames = (groupIds: number[] = []) => {
    if (groupIds.length === 0) return "No groups";
    
    return groupIds
      .map(id => {
        const group = documentGroups.find(g => g.id === id);
        return group ? group.name : "";
      })
      .filter(Boolean)
      .join(", ");
  };

  const getDocumentSource = (documentTypeId: number) => {
    const documentType = documentTypes.find(type => type.id === documentTypeId);
    return documentType?.source === 'file' ? 'File' : 'Platform';
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    setSearchParams(prev => ({
      ...prev,
      offset: (page - 1) * (prev.limit || 10)
    }));
  };

  const handleDeleteClick = (document: Document) => {
    setDeletingDocument(document);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deletingDocument) return;
    setIsDeleting(true);
    try {
      await documentApi.delete(deletingDocument.id);
      setDeleteDialogOpen(false);
      setDeletingDocument(null);
      toast({
        title: "Document deleted",
        description: "The document has been removed.",
        duration: 3000,
      });
      refetch();
    } catch (err: any) {
      // Handle specific error for documents referenced in orders
      if (err?.status === 409 || err?.message?.includes('referenced in existing orders')) {
        toast({
          title: "Cannot delete document",
          description: "This document is currently used in existing orders and cannot be deleted. To remove this document, you must first remove it from all associated orders.",
          variant: "destructive",
          duration: 5000,
        });
      } else {
        toast({
          title: "Failed to delete document",
          description: err?.message || "An error occurred while deleting the document",
          variant: "destructive",
        });
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDuplicateClick = (document: Document) => {
    setDuplicatingDocument(document);
    setNewDocumentName(`Copy of ${document.name}`);
    setDuplicateDialogOpen(true);
  };

  const handleConfirmDuplicate = async () => {
    if (!duplicatingDocument || !newDocumentName.trim()) return;
    
    setIsDuplicating(true);
    try {
      await enhancedDocumentApi.duplicate(duplicatingDocument.id, newDocumentName);
      
      setDuplicateDialogOpen(false);
      setDuplicatingDocument(null);
      setNewDocumentName("");
      
      toast({
        title: "Document duplicated",
        description: "The document has been duplicated successfully.",
        duration: 3000,
      });
      
      refetch();
    } catch (err: any) {
      toast({
        title: "Failed to duplicate document",
        description: err?.message || "An error occurred while duplicating the document",
        variant: "destructive",
      });
    } finally {
      setIsDuplicating(false);
    }
  };

  // Show loading state while permissions are loading
  if (loading || !permissionsLoaded) {
    return (
      <BackofficeLayout>
        <div className="flex items-center justify-center h-64">
          <div className="flex flex-col items-center space-y-4">
            <Loader2 className="h-8 w-8 animate-spin text-brand-blue" />
            <p className="text-sm text-gray-500">
              {loading ? "Loading..." : "Loading permissions..."}
            </p>
          </div>
        </div>
      </BackofficeLayout>
    );
  }

  return (
    <BackofficeLayout>
      <div className="container mx-auto py-10">
        <div className="mb-6">
          <div className="flex justify-between items-center mb-4">
            <div className="text-2xl font-bold flex items-center gap-2">
              <FileText className="h-6 w-6" />
              Documents
            </div>
            <div className="flex items-center gap-2">
              <DocumentExport 
                searchParams={searchParams}
              />
              {canWriteDocument() && (
                <Button asChild>
                  <Link to="/backoffice/documents/new">
                    <Plus className="mr-2 h-4 w-4" />
                    Add Document
                  </Link>
                </Button>
              )}
            </div>
          </div>
          
          <div className="flex flex-col space-y-4 md:space-y-0 md:flex-row md:items-center gap-4">
            <DocumentGroupFilter 
              values={searchParams.document_group_ids || []} 
              onChange={handleGroupFilterChange} 
            />
            <DocumentTypeFilter
              value={searchParams.document_type_id}
              onChange={handleTypeFilterChange}
            />
            <DocumentStatusFilter
              value={searchParams.status}
              onChange={handleStatusFilterChange}
            />
            <Input
              type="search"
              placeholder="Search by document name..."
              className="md:w-[300px]"
              onChange={handleSearchChange}
              value={searchText}
            />
          </div>
        </div>

        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Document Groups</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={4}>
                    <div className="flex justify-center py-4">
                      <Loader2 className="h-8 w-8 animate-spin text-brand-blue" />
                    </div>
                  </TableCell>
                </TableRow>
              ) : documents.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="h-24 text-center">
                    No documents found
                  </TableCell>
                </TableRow>
              ) : (
                documents.map((document) => (
                  <TableRow key={document.id}>
                    <TableCell>
                      <div className="font-medium">{document.name}</div>
                      <div className="text-sm text-muted-foreground">
                        {getDocumentSource(document.document_type_id)}
                      </div>
                    </TableCell>
                    <TableCell>
                      {getDocumentGroupNames(document.document_groups)}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        {/* Document Status */}
                        {document.status === 'expired' ? (
                          <Badge variant="outline" className="text-red-600 border-red-200 flex w-fit items-center">
                            <FileX className="mr-1 h-3 w-3" />
                            Expired
                          </Badge>
                        ) : document.status === 'missing' ? (
                          <Badge variant="outline" className="text-orange-600 border-orange-200 flex w-fit items-center">
                            <FileQuestion className="mr-1 h-3 w-3" />
                            Missing
                          </Badge>
                        ) : (
                          <Badge className="bg-green-100 text-green-800 hover:bg-green-200 flex w-fit items-center">
                            <FileCheck className="mr-1 h-3 w-3" />
                            Active
                          </Badge>
                        )}
                        
                        {/* Public Availability */}
                        <div className="text-xs text-muted-foreground">
                          {document.publicly_available ? "Publicly Available" : "Not Public"}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreHorizontal className="h-4 w-4" />
                            <span className="sr-only">Open menu</span>
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => navigate(`/backoffice/documents/${document.id}`)}>
                            <Eye className="mr-2 h-4 w-4" />
                            View
                          </DropdownMenuItem>
                          {canWriteDocument() && (
                            <>
                              <DropdownMenuItem onClick={() => navigate(`/backoffice/documents/${document.id}/edit`)}>
                                <FilePen className="mr-2 h-4 w-4" />
                                Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleDuplicateClick(document)}>
                                <Copy className="mr-2 h-4 w-4" />
                                Duplicate
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem className="text-destructive" onClick={() => handleDeleteClick(document)}>
                                <Trash2 className="mr-2 h-4 w-4" />
                                Delete
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
        
        {!isLoading && totalPages > 1 && (
          <div className="mt-4">
            <PaginationControls
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </div>

      {/* Duplicate Document Dialog */}
      <Dialog open={duplicateDialogOpen} onOpenChange={setDuplicateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Duplicate Document</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <label htmlFor="documentName" className="block text-sm font-medium text-gray-700 mb-1">
              New Document Name
            </label>
            <Input
              id="documentName"
              value={newDocumentName}
              onChange={(e) => setNewDocumentName(e.target.value)}
              placeholder="Enter document name"
              disabled={isDuplicating}
              className="w-full"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setDuplicateDialogOpen(false);
                setDuplicatingDocument(null);
                setNewDocumentName("");
              }}
              disabled={isDuplicating}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleConfirmDuplicate}
              disabled={isDuplicating || !newDocumentName.trim()}
            >
              {isDuplicating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Duplicating...
                </>
              ) : (
                'Duplicate'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DeleteDocumentDialog
        open={deleteDialogOpen}
        loading={isDeleting}
        documentName={deletingDocument?.name}
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          setDeleteDialogOpen(false);
          setDeletingDocument(null);
        }}
      />
    </BackofficeLayout>
  );
};

export default Documents;
